/**
 * Spreadsheet parsing for the unit import (#21).
 *
 * Same dynamic SheetJS load as the lead importer, and the same reason: a rep on a
 * 3G connection should never download a parser they will not use. See
 * src/features/leads/importSheet.ts for the note on why this dependency comes from
 * the SheetJS CDN rather than the npm registry.
 *
 * The difference from the lead importer is what happens to a bad row. A lead sheet
 * is 500 unrelated people, so good rows are let through. A unit sheet is one
 * developer's price list, so one bad row rejects the file — import_units() enforces
 * that, and this parser only has to hand it faithful strings.
 */

import type { UnitType } from '@/types/app';

const HEADER_ALIASES: Record<string, string[]> = {
  unit_code: ['unit_code', 'code', 'unit', 'الكود', 'كود الوحدة', 'رقم الوحدة', 'الوحدة'],
  type: ['type', 'unit_type', 'النوع', 'نوع الوحدة'],
  bedrooms: ['bedrooms', 'beds', 'rooms', 'الغرف', 'عدد الغرف', 'غرف النوم'],
  bathrooms: ['bathrooms', 'baths', 'الحمامات', 'عدد الحمامات'],
  floor: ['floor', 'الدور', 'الطابق'],
  view: ['view', 'الإطلالة', 'الفيو'],
  area_sqm: ['area_sqm', 'area', 'size', 'sqm', 'المساحة', 'المساحة بالمتر'],
  price_total: ['price_total', 'price', 'total_price', 'السعر', 'السعر الإجمالي', 'الإجمالي'],
  down_payment: ['down_payment', 'down', 'deposit', 'المقدم', 'المقدّم', 'الدفعة المقدمة'],
  installment_years: ['installment_years', 'years', 'سنوات التقسيط', 'مدة التقسيط', 'السنوات'],
  monthly_installment: ['monthly_installment', 'monthly', 'installment', 'القسط', 'القسط الشهري'],
  delivery_date: ['delivery_date', 'delivery', 'التسليم', 'تاريخ التسليم'],
};

/**
 * Arabic unit types, mapped to the enum before the sheet reaches the database.
 *
 * Without this every Arabic price list — which is most of them — fails validation on
 * row 1 with `type "شقة" is not one of: apartment, villa, ...`, and the importer is
 * useless to the people it was built for. The mapping belongs here rather than in
 * SQL: it is a presentation concern, and the enum stays the single source of truth.
 */
const TYPE_ALIASES: Record<string, UnitType> = {
  شقة: 'apartment',
  شقه: 'apartment',
  apartment: 'apartment',
  flat: 'apartment',
  فيلا: 'villa',
  villa: 'villa',
  'تاون هاوس': 'townhouse',
  تاونهاوس: 'townhouse',
  townhouse: 'townhouse',
  'توين هاوس': 'twinhouse',
  توينهاوس: 'twinhouse',
  twinhouse: 'twinhouse',
  دوبلكس: 'duplex',
  duplex: 'duplex',
  استوديو: 'studio',
  ستوديو: 'studio',
  studio: 'studio',
  شاليه: 'chalet',
  chalet: 'chalet',
  مكتب: 'office',
  office: 'office',
  اداري: 'office',
  محل: 'retail',
  'محل تجاري': 'retail',
  retail: 'retail',
  تجاري: 'retail',
  عيادة: 'clinic',
  clinic: 'clinic',
  طبي: 'clinic',
};

const normalise = (value: string) =>
  String(value)
    .trim()
    .toLowerCase()
    // Strip the bidi marks Excel sprinkles into RTL headers, and collapse the
    // alef/yaa variants people type interchangeably.
    .replace(/[‎‏‪-‮]/g, '')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/\s+/g, ' ');

function mapHeader(header: string): string | null {
  const key = normalise(header);
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.some((alias) => normalise(alias) === key)) return field;
  }
  return null;
}

const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';

export interface ParsedUnitSheet {
  rows: Record<string, string>[];
  unmappedHeaders: string[];
  matchedFields: string[];
}

export async function parseUnitSheet(file: File): Promise<ParsedUnitSheet> {
  const XLSX = await import('xlsx');

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheet = workbook.SheetNames[0];
  if (!firstSheet) return { rows: [], unmappedHeaders: [], matchedFields: [] };

  // raw:false so prices and dates arrive as the strings the file displays, not as
  // Excel serial numbers. import_units() validates by casting, so a value it cannot
  // parse is reported against its row rather than aborting the import.
  const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[firstSheet]!, {
    raw: false,
    defval: '',
  });

  const unmapped = new Set<string>();
  const matched = new Set<string>();

  const rows = raw.map((row) => {
    const out: Record<string, string> = {};

    for (const [header, value] of Object.entries(row)) {
      const field = mapHeader(header);
      if (!field) {
        unmapped.add(header);
        continue;
      }
      matched.add(field);

      // A merged or error cell arrives as an object; String() would import it as
      // the literal "[object Object]".
      let text =
        typeof value === 'string'
          ? value.trim()
          : typeof value === 'number' || typeof value === 'boolean'
            ? String(value)
            : '';

      if (field === 'type' && text) {
        text = TYPE_ALIASES[normalise(text)] ?? text;
      }

      // Money is routinely written "2,500,000" or "2 500 000". Strip the separators
      // rather than letting a good price list fail on punctuation — anything else
      // non-numeric still reaches the validator and is reported against its row.
      if (
        (field === 'price_total' || field === 'down_payment' || field === 'monthly_installment') &&
        text
      ) {
        const stripped = text
          .replace(/[,\s]/g, '')
          .replace(/[٠-٩]/g, (d) => String(ARABIC_DIGITS.indexOf(d)));
        if (/^\d+(\.\d+)?$/.test(stripped)) text = stripped;
      }

      out[field] = text;
    }

    return out;
  });

  return {
    rows: rows.filter((row) => Object.values(row).some((v) => v !== '')),
    unmappedHeaders: [...unmapped],
    matchedFields: [...matched],
  };
}

/** A template, so the expected shape is a download rather than a guess. */
export async function downloadUnitTemplate(): Promise<void> {
  const XLSX = await import('xlsx');

  const sheet = XLSX.utils.json_to_sheet([
    {
      unit_code: 'A-101',
      type: 'apartment',
      bedrooms: '3',
      bathrooms: '2',
      floor: '4',
      view: 'لاند سكيب',
      area_sqm: '160',
      price_total: '2500000',
      down_payment: '400000',
      installment_years: '8',
      monthly_installment: '21875',
      delivery_date: '2027-06-01',
    },
  ]);

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'units');
  XLSX.writeFile(book, 'units-template.xlsx');
}
